/**
 * Debounced sync scheduler.
 *
 * Separate from sync.ts to avoid circular dependency (db.ts → sync.ts → db.ts).
 * Calling `scheduleSyncFlush()` after enqueuing an operation ensures that
 * pending changes are pushed to the server within a short window, without
 * needing a page reload or reconnect event.
 */

import { Sentry } from "./sentry";

let _timer: ReturnType<typeof setTimeout> | null = null;
const DEBOUNCE_MS = 1_500;

/**
 * Schedule a `fullSync()` after a short debounce. Multiple calls within the
 * window are coalesced so rapid edits don't hammer the API.
 */
export function scheduleSyncFlush(): void {
  if (!navigator.onLine) return;
  if (_timer) clearTimeout(_timer);
  _timer = setTimeout(async () => {
    _timer = null;
    try {
      // Dynamic import to break the circular dependency
      const { fullSync } = await import("./sync");
      await fullSync();
    } catch (err) {
      // fullSync rethrows what it could not handle: unexpected unless the
      // browser went offline meanwhile.
      if (navigator.onLine) Sentry.captureException(err)
    }
  }, DEBOUNCE_MS);
}
